import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ChemicalsService } from '../../../services/chemicals.service';
import { SaveChemicalDto } from '@models/save-chemical-dto.interface';
import { MaterialSubTypeLookupDto } from '@models/edit-chemical-dto.interface';

export interface EditChemicalDialogData {
  chemicalId?: number;
  mode: 'create' | 'edit';
}

@Component({
  selector: 'app-edit-chemical',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './edit-chemical.component.html',
  styleUrl: './edit-chemical.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditChemicalComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<EditChemicalComponent>);
  protected readonly data = inject<EditChemicalDialogData>(MAT_DIALOG_DATA);
  private readonly chemicalsService = inject(ChemicalsService);
  private readonly fb = inject(FormBuilder);

  protected isLoading = signal(true);
  protected isSaving = signal(false);
  protected errorMessage = signal('');
  protected isCreateMode = this.data.mode === 'create';
  protected materialTypes = signal<{ id: number; name: string }[]>([]);
  protected allMaterialSubTypes = signal<MaterialSubTypeLookupDto[]>([]);

  protected filteredSubTypes = signal<MaterialSubTypeLookupDto[]>([]);

  protected form = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(200)]],
    materialTypeId: [0, [Validators.required, Validators.min(1)]],
    materialSubTypeId: [null as number | null],
    specificGravity: [null as number | null],
    density: [null as number | null],
    mixingTime: [null as number | null],
  });

  protected focusNextInput(event: KeyboardEvent): void {
    const form = event.currentTarget as HTMLFormElement;
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const editableControls = Array.from(form.querySelectorAll<HTMLElement>(
      'input[formControlName]:not([type="hidden"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([readonly]):not([disabled]):not([tabindex="-1"]), select[formControlName]:not([disabled]):not([tabindex="-1"])'
    ));
    const currentIndex = editableControls.indexOf(target);
    if (currentIndex < 0) return;

    event.preventDefault();
    if (currentIndex < editableControls.length - 1) {
      editableControls[currentIndex + 1].focus();
    }
  }

  private readonly PPG_FACTOR = 8.345;
  private syncing = false;

  ngOnInit(): void {
    this.form.get('specificGravity')!.valueChanges.subscribe(sg => {
      if (this.syncing) return;
      this.syncing = true;
      this.form.patchValue(
        { density: sg != null ? Math.round(sg * this.PPG_FACTOR * 1000) / 1000 : null },
        { emitEvent: false }
      );
      this.syncing = false;
    });

    this.form.get('density')!.valueChanges.subscribe(d => {
      if (this.syncing) return;
      this.syncing = true;
      this.form.patchValue(
        { specificGravity: d != null ? Math.round((d / this.PPG_FACTOR) * 1000) / 1000 : null },
        { emitEvent: false }
      );
      this.syncing = false;
    });

    const request$ = this.isCreateMode
      ? this.chemicalsService.getForCreate()
      : this.chemicalsService.getForEdit(this.data.chemicalId!);

    request$.subscribe({
      next: (chemical) => {
        this.materialTypes.set(chemical.materialTypes);
        this.allMaterialSubTypes.set(chemical.materialSubTypes);
        this.filteredSubTypes.set(chemical.materialSubTypes.filter(s => s.materialTypeId === chemical.materialTypeId));

        this.form.patchValue({
          name: chemical.name,
          materialTypeId: chemical.materialTypeId,
          materialSubTypeId: chemical.materialSubTypeId,
          specificGravity: chemical.specificGravity,
          density: chemical.specificGravity != null
            ? Math.round(chemical.specificGravity * this.PPG_FACTOR * 1000) / 1000
            : null,
          mixingTime: chemical.mixingTime,
        });

        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false),
    });
  }

  protected onTypeChange(): void {
    this.form.patchValue({ materialSubTypeId: null });
    const typeId = Number(this.form.get('materialTypeId')?.value);
    this.filteredSubTypes.set(this.allMaterialSubTypes().filter(s => s.materialTypeId === typeId));
  }

  protected onSave(): void {
    if (this.form.invalid) return;

    const raw = this.form.getRawValue();
    const dto: SaveChemicalDto = {
      name: raw.name ?? '',
      materialTypeId: Number(raw.materialTypeId),
      materialSubTypeId: raw.materialSubTypeId ?? null,
      specificGravity: raw.specificGravity != null ? Number(raw.specificGravity) : null,
      mixingTime: raw.mixingTime != null ? Number(raw.mixingTime) : null,
    };

    this.isSaving.set(true);
    this.errorMessage.set('');
    const save$: Observable<unknown> = this.isCreateMode
      ? this.chemicalsService.create(dto)
      : this.chemicalsService.update(this.data.chemicalId!, dto);

    save$.subscribe({
      next: () => {
        this.isSaving.set(false);
        this.dialogRef.close(true);
      },
      error: (err: HttpErrorResponse) => {
        this.isSaving.set(false);
        this.errorMessage.set(this.extractErrorMessage(err));
      },
    });
  }

  private extractErrorMessage(err: HttpErrorResponse): string {
    const nameErrors = err.error?.errors?.name;
    if (Array.isArray(nameErrors) && nameErrors.length > 0) {
      return nameErrors[0];
    }
    return 'Save failed. Please check your entries and try again.';
  }

  protected onCancel(): void {
    this.dialogRef.close(false);
  }
}
